import { useState, useEffect, useCallback, useRef } from 'react'
import { getNotes } from '../../api/notes'
import { getTags } from '../../api/tags'
import type { Note } from '../../api/notes'
import type { Tag } from '../../api/tags'

interface ContextBagProps {
  mode: 'smart_search' | 'manual'
  pinnedNoteIds: string[]
  onModeChange: (mode: 'smart_search' | 'manual') => void
  onPinnedNotesChange: (ids: string[]) => void
  remainingTokens: number
  tokensRefreshTime: number | null
}

export default function ContextBag({
  mode,
  pinnedNoteIds,
  onModeChange,
  onPinnedNotesChange,
  remainingTokens,
}: ContextBagProps) {
  const [noteSearch, setNoteSearch] = useState('')
  const [filterTags, setFilterTags] = useState<Tag[]>([])
  const [availableTags, setAvailableTags] = useState<Tag[]>([])
  const [notes, setNotes] = useState<Note[]>([])
  const [notesLoading, setNotesLoading] = useState(false)
  // Use a ref for skip to avoid stale closures in loadNotes
  const notesSkipRef = useRef(0)
  const [notesTotal, setNotesTotal] = useState(0)
  const NOTES_LIMIT = 20

  // Load tags once
  useEffect(() => {
    getTags().then(setAvailableTags).catch(() => {})
  }, [])

  // Load / reload notes when search or filter changes
  const loadNotes = useCallback(async (reset = true) => {
    setNotesLoading(true)
    if (reset) notesSkipRef.current = 0
    const skip = notesSkipRef.current
    try {
      const result = await getNotes(
        {
          search_keyword: noteSearch || undefined,
          selected_tags: filterTags.length > 0 ? filterTags : undefined,
          view_status: 'active',
        },
        'modified_time',
        'desc',
        skip,
        NOTES_LIMIT
      )
      setNotes((prev) => (reset ? result.data : [...prev, ...result.data]))
      notesSkipRef.current = skip + result.data.length
      setNotesTotal(result.count)
    } catch {
      // silently ignore
    } finally {
      setNotesLoading(false)
    }
  }, [noteSearch, filterTags])

  useEffect(() => {
    const t = setTimeout(() => loadNotes(true), 300)
    return () => clearTimeout(t)
  }, [noteSearch, filterTags]) // eslint-disable-line react-hooks/exhaustive-deps

  function togglePin(noteId: string) {
    if (pinnedNoteIds.includes(noteId)) {
      onPinnedNotesChange(pinnedNoteIds.filter((id) => id !== noteId))
    } else {
      onPinnedNotesChange([...pinnedNoteIds, noteId])
    }
  }

  function toggleFilterTag(tag: Tag) {
    const exists = filterTags.find((t) => t.id === tag.id)
    setFilterTags(exists ? filterTags.filter((t) => t.id !== tag.id) : [...filterTags, tag])
    notesSkipRef.current = 0
  }

  function formatTokens(n: number) {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`
    return n.toString()
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          Context Bag
        </h3>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Mode toggle */}
        <div className="p-4 border-b border-gray-100">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Mode</p>
          <div className="space-y-2">
            {(['smart_search', 'manual'] as const).map((m) => (
              <label
                key={m}
                data-testid={`context-mode-${m}`}
                className="flex items-start gap-3 p-2.5 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <input
                  type="radio"
                  name="mode"
                  value={m}
                  checked={mode === m}
                  onChange={() => onModeChange(m)}
                  className="mt-0.5 accent-primary"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {m === 'smart_search' ? '🔍 Smart Search' : '📌 Manual'}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {m === 'smart_search'
                      ? 'AI automatically searches across all your notes'
                      : 'Only use the notes you explicitly attach below'}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Smart Search info — shown instead of notes browser */}
        {mode === 'smart_search' && (
          <div className="p-4">
            <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-3 flex items-start gap-2.5">
              <svg className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs text-blue-700 leading-relaxed">
                The AI will automatically search <span className="font-semibold">all your notes</span> to find relevant context for your question. Switch to <span className="font-semibold">Manual</span> to hand-pick specific notes.
              </p>
            </div>
          </div>
        )}

        {/* Manual mode: pinned notes summary + notes browser */}
        {mode === 'manual' && (
          <>
            {/* Pinned notes summary */}
            {pinnedNoteIds.length > 0 && (
              <div className="px-4 py-3 border-b border-gray-100 bg-green-50">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-green-800">
                    {pinnedNoteIds.length} note{pinnedNoteIds.length !== 1 ? 's' : ''} in context
                  </p>
                  <button
                    onClick={() => onPinnedNotesChange([])}
                    className="text-xs text-green-600 hover:text-green-800 hover:underline"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}

            {/* Notes browser */}
            <div className="p-4 space-y-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Attach Notes</p>

              {/* Tag filters */}
              {availableTags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {availableTags.slice(0, 12).map((tag) => {
                    const active = filterTags.some((t) => t.id === tag.id)
                    return (
                      <button
                        key={tag.id}
                        onClick={() => toggleFilterTag(tag)}
                        className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                          active ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {tag.name}
                      </button>
                    )
                  })}
                </div>
              )}

              {/* Note search */}
              <div className="relative">
                <svg className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search notes…"
                  value={noteSearch}
                  onChange={(e) => { setNoteSearch(e.target.value); notesSkipRef.current = 0 }}
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                />
              </div>

              {/* Notes list */}
              <div className="space-y-1">
                {notesLoading && notes.length === 0 ? (
                  <div className="flex justify-center py-4">
                    <div className="w-5 h-5 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
                  </div>
                ) : notes.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-4">No notes found</p>
                ) : (
                  notes.map((note) => {
                    const isPinned = pinnedNoteIds.includes(note.id)
                    return (
                      <label
                        key={note.id}
                        data-testid="context-note-item"
                        className={`flex items-start gap-2.5 p-2 rounded-lg cursor-pointer transition-colors ${
                          isPinned ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isPinned}
                          onChange={() => togglePin(note.id)}
                          className="mt-0.5 accent-primary flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{note.name}</p>
                          {note.tags.length > 0 && (
                            <p className="text-xs text-gray-400 truncate">
                              {note.tags.map((t) => t.name).join(', ')}
                            </p>
                          )}
                        </div>
                      </label>
                    )
                  })
                )}
              </div>

              {/* Load more */}
              {notes.length < notesTotal && !notesLoading && (
                <button
                  onClick={() => loadNotes(false)}
                  className="w-full py-2 text-xs text-primary hover:text-secondary font-medium"
                >
                  Load more ({notesTotal - notes.length} remaining)
                </button>
              )}
              {notesLoading && notes.length > 0 && (
                <div className="flex justify-center py-2">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
                </div>
              )}
            </div>
          </>
        )}

      </div>{/* end flex-1 overflow-y-auto */}

      {/* Token display at bottom */}
      <div className="px-4 py-3 border-t border-gray-200 flex-shrink-0 bg-gray-50">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Tokens remaining</span>
          <span className={`text-xs font-semibold ${remainingTokens <= 0 ? 'text-red-600' : 'text-gray-700'}`}>
            {formatTokens(remainingTokens)}
          </span>
        </div>
        {remainingTokens <= 0 && (
          <a href="/subscription" className="text-xs text-primary hover:underline">
            Upgrade for more tokens →
          </a>
        )}
      </div>
    </div>
  )
}

